import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/apiClient";
import type { AppTicketDetail, AppTicketListItem } from "../api/types";
import { Modal } from "../components/Modal";

const DEFAULT_STATUSES = ["Have not started", "In process", "Done"] as const;

function formatTicketDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function orderedStatusColumns(items: AppTicketListItem[]): string[] {
  const set = new Set<string>(DEFAULT_STATUSES);
  const extras = items.map((t) => t.status).filter((s) => !set.has(s));
  return [...DEFAULT_STATUSES, ...[...new Set(extras)]];
}

const CUSTOM_STATUS_VALUE = "__custom_ticket_status";

function mergeStatusOptions(items: AppTicketListItem[], detail?: AppTicketDetail | null): string[] {
  const base = orderedStatusColumns(items);
  if (detail && !base.includes(detail.status)) {
    return [...base, detail.status];
  }
  return base;
}

export function FeedbackTicketsPage() {
  const [tickets, setTickets] = useState<AppTicketListItem[]>([]);
  const ticketsRef = useRef<AppTicketListItem[]>([]);
  ticketsRef.current = tickets;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AppTicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [titleDraft, setTitleDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [metaSaving, setMetaSaving] = useState(false);

  const [statusSelect, setStatusSelect] = useState("");
  const [customStatus, setCustomStatus] = useState("");
  const [statusPatching, setStatusPatching] = useState(false);

  const [commentBody, setCommentBody] = useState("");
  const [commentPosting, setCommentPosting] = useState(false);

  const loadTickets = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const rows = await api.listTickets();
      setTickets(rows);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  const reloadDetail = useCallback(async (id: string) => {
    setDetailError(null);
    setDetailLoading(true);
    try {
      const row = await api.getTicket(id);
      setDetail(row);
      setTitleDraft(row.title);
      setDescriptionDraft(row.description);
      const presets = mergeStatusOptions(ticketsRef.current, row);
      if (presets.includes(row.status)) {
        setStatusSelect(row.status);
        setCustomStatus("");
      } else {
        setStatusSelect(CUSTOM_STATUS_VALUE);
        setCustomStatus(row.status);
      }
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : "Could not load ticket");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    void reloadDetail(detailId);
  }, [detailId, reloadDetail]);
  const columns = useMemo(() => orderedStatusColumns(tickets), [tickets]);

  /** Group tickets: each column owns its exact status matches (including extras). */
  const cardsForColumn = useCallback(
    (statusCol: string) => tickets.filter((t) => t.status === statusCol),
    [tickets],
  );

  const openDetail = (id: string) => setDetailId(id);
  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setCommentBody("");
    setDetailError(null);
  };

  async function saveTicketMeta() {
    if (!detailId || !detail) return;
    const title = titleDraft.trim();
    const description = descriptionDraft.trim();
    if (!title || !description) {
      setDetailError("Title and description cannot be empty");
      return;
    }
    const sameTitle = title === detail.title && description === detail.description;
    if (sameTitle) return;

    setMetaSaving(true);
    setDetailError(null);
    try {
      const updated = await api.patchTicket(detailId, { title, description });
      setDetail((d) =>
        d
          ? { ...d, title: updated.title, description: updated.description, updatedAt: updated.updatedAt }
          : null,
      );
      setTickets((prev) =>
        prev.map((t) => (t.id === detailId ? { ...t, ...updated } : t)),
      );
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setMetaSaving(false);
    }
  }

  async function patchStatus(nextRaw: string) {
    const next = nextRaw.trim();
    if (!detailId || !detail || !next) return;
    if (next === detail.status) return;

    setStatusPatching(true);
    setDetailError(null);
    try {
      const updated = await api.patchTicket(detailId, { status: next });
      setTickets((prev) => {
        const mapped = prev.map((t) => (t.id === detailId ? { ...t, ...updated } : t));
        ticketsRef.current = mapped;
        return mapped;
      });
      const detailAfterUpdate: AppTicketDetail = {
        ...detail,
        status: updated.status,
        updatedAt: updated.updatedAt,
      };
      setDetail(detailAfterUpdate);

      const opts = mergeStatusOptions(ticketsRef.current, detailAfterUpdate);
      if (opts.includes(updated.status)) {
        setStatusSelect(updated.status);
        setCustomStatus("");
      } else {
        setStatusSelect(CUSTOM_STATUS_VALUE);
        setCustomStatus(updated.status);
      }
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : "Could not update status");
    } finally {
      setStatusPatching(false);
    }
  }
  async function postComment() {
    if (!detailId) return;
    const content = commentBody.trim();
    if (!content) return;
    setCommentPosting(true);
    setDetailError(null);
    try {
      const comment = await api.addTicketComment(detailId, { content });
      setCommentBody("");
      setDetail((d) => (d ? { ...d, comments: [...d.comments, comment] } : null));
      setTickets((prev) =>
        prev.map((t) =>
          t.id === detailId ? { ...t, _count: { comments: t._count.comments + 1 } } : t,
        ),
      );
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : "Could not post comment");
    } finally {
      setCommentPosting(false);
    }
  }

  const statusChoices = useMemo(
    () => mergeStatusOptions(tickets, detail),
    [tickets, detail],
  );

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <div>
          <h2 className="page-title">Product feedback</h2>
          <p className="page-subtitle">
            Feature requests, bugs, and UI notes while inventory is in rollout. Visible to shop owners only.
          </p>
        </div>
        <button type="button" className="button" onClick={() => setNewOpen(true)}>
          New ticket
        </button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {loading ? <div className="muted">Loading…</div> : null}

      <div
        className="feedback-kanban"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "var(--space-3)",
          alignItems: "start",
        }}
      >
        {columns.map((col) => (
          <div
            key={col}
            className="card"
            style={{
              padding: "var(--space-3)",
              background: "var(--color-bg)",
              boxShadow: "var(--shadow-1)",
              minHeight: "200px",
            }}
          >
            <h3
              style={{
                margin: "0 0 var(--space-2)",
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--color-text-muted)",
              }}
            >
              {col}
            </h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              {cardsForColumn(col).map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="card"
                    onClick={() => openDetail(t.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "var(--space-3)",
                      border: "none",
                      cursor: "pointer",
                      background: "var(--color-surface)",
                      transition: "transform var(--transition-fast)",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: "var(--space-1)", color: "var(--color-text)" }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                      By {t.createdBy.fullName} · {formatTicketDate(t.createdAt)}
                    </div>
                    {t._count.comments > 0 ? (
                      <span className="badge muted" style={{ marginTop: "var(--space-2)", display: "inline-block" }}>
                        {t._count.comments} note{t._count.comments === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>



      {newOpen ? (
        <NewTicketModal
          onClose={() => setNewOpen(false)}
          onCreated={(created) => {
            setTickets((prev) => [created, ...prev]);
            setNewOpen(false);
            openDetail(created.id);
          }}
        />
      ) : null}

      {detailId ? (
        <Modal title={detail?.title ?? "Ticket"} onClose={closeDetail}>
          {detailLoading ? <p className="muted">Loading…</p> : null}
          {detailError ? <div className="alert error">{detailError}</div> : null}

          {detail ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              <p className="muted" style={{ margin: 0 }}>
                Created {formatTicketDate(detail.createdAt)} · {detail.createdBy.fullName}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                <label htmlFor="ticket-title" className="field-label">
                  Title
                </label>
                <input
                  id="ticket-title"
                  className="input"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                <label htmlFor="ticket-desc" className="field-label">
                  Description
                </label>
                <textarea
                  id="ticket-desc"
                  className="textarea"
                  rows={5}
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="button tinted"
                onClick={() => void saveTicketMeta()}
                disabled={metaSaving}
              >
                {metaSaving ? "Saving…" : "Save title & description"}
              </button>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "flex-end" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 200px" }}>
                  <label htmlFor="ticket-status" className="field-label">
                    Status
                  </label>
                  <select
                    id="ticket-status"
                    className="select"
                    value={statusSelect}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStatusSelect(v);
                      if (v !== CUSTOM_STATUS_VALUE) {
                        void patchStatus(v);
                      }
                    }}
                  >
                    {statusChoices.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value={CUSTOM_STATUS_VALUE}>Custom…</option>
                  </select>
                </div>
                {statusSelect === CUSTOM_STATUS_VALUE ? (
                  <>
                    <input
                      className="input"
                      placeholder="Enter status label"
                      value={customStatus}
                      onChange={(e) => setCustomStatus(e.target.value)}
                      style={{ flex: "1 1 180px", minWidth: "120px" }}
                    />
                    <button
                      type="button"
                      className="button"
                      disabled={statusPatching}
                      onClick={() => void patchStatus(customStatus)}
                    >
                      Apply status
                    </button>
                  </>
                ) : null}
              </div>
              {statusPatching ? <span className="muted">Updating status…</span> : null}

              <div>
                <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "16px" }}>Comments</h3>
                <ul style={{ listStyle: "none", margin: "0 0 var(--space-3)", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  {detail.comments.map((c) => (
                    <li
                      key={c.id}
                      className="card"
                      style={{
                        padding: "var(--space-3)",
                        background: "var(--color-bg)",
                        margin: 0,
                      }}
                    >
                      <div className="muted" style={{ fontSize: "13px", marginBottom: "var(--space-1)" }}>
                        {c.createdBy.fullName} · {formatTicketDate(c.createdAt)}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", color: "var(--color-text)" }}>{c.content}</div>
                    </li>
                  ))}
                  {detail.comments.length === 0 ? (
                    <li className="muted">No comments yet.</li>
                  ) : null}
                </ul>
                <textarea
                  className="textarea"
                  rows={3}
                  placeholder="Reply or add notes…"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                />
                <button
                  type="button"
                  className="button"
                  style={{ marginTop: "var(--space-2)" }}
                  disabled={commentPosting || !commentBody.trim()}
                  onClick={() => void postComment()}
                >
                  {commentPosting ? "Posting…" : "Post comment"}
                </button>
              </div>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </section>
  );
}

function NewTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (t: AppTicketListItem) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    const d = description.trim();
    if (!t || !d) return;

    setSubmitting(true);
    setErr(null);
    try {
      const created = await api.createTicket({ title: t, description: d });
      onCreated(created);
      setTitle("");
      setDescription("");
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Could not create ticket");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="New ticket" onClose={onClose}>
      <form
        className="new-ticket-form"
        onSubmit={(e) => void submit(e)}
      >
        {err ? <div className="alert error">{err}</div> : null}
        <div className="field">
          <label htmlFor="new-title" className="visually-hidden">
            Title
          </label>
          <input
            id="new-title"
            type="text"
            autoComplete="off"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="new-desc" className="visually-hidden">
            Description
          </label>
          <textarea
            id="new-desc"
            rows={6}
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="new-ticket-form-actions">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={submitting || !title.trim() || !description.trim()}>
            {submitting ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
